import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import ManualSelectionsBanner from '../components/ManualSelectionsBanner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import {
  Loader2,
  Link as LinkIcon,
  Copy,
  ExternalLink,
  Eye,
  Ban,
  CheckCircle2,
  XCircle,
  Calendar,
  Clock,
  Users,
  Building2,
  FileText,
  Plus,
  RefreshCw,
  Search,
  Shield,
  Lock,
  Globe,
  ChevronRight,
  AlertTriangle,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

export default function PublicCVLinksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';
  
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requirements, setRequirements] = useState([]);
  const [clients, setClients] = useState([]);
  const [dossiers, setDossiers] = useState([]);
  
  // Create link modal
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLink, setNewLink] = useState({
    requirement_id: '',
    client_id: '',
    candidate_ids: [],
    expires_in_days: 7,
    password: ''
  });
  
  // Detail drawer
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [decisions, setDecisions] = useState([]);
  const [loadingDecisions, setLoadingDecisions] = useState(false);
  
  // Filters
  const [search, setSearch] = useState('');
  const [requirementFilter, setRequirementFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('ats_token');

  const loadLinks = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString(), page_size: '20' });
      if (requirementFilter !== 'all') params.append('requirement_id', requirementFilter);
      
      const response = await fetch(`${API_URL}/api/manual-selections/public-cv-links?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLinks(data.links);
        setTotalPages(data.total_pages);
      }
    } catch (error) {
      console.error('Failed to load links:', error);
      toast.error('Failed to load public links');
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, page, requirementFilter]);

  const loadRequirements = async () => {
    try {
      const response = await fetch(`${API_URL}/api/requirements?page_size=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setRequirements(data.requirements || []);
      }
    } catch (error) {
      console.error('Failed to load requirements:', error);
    }
  };

  const loadClients = async () => {
    try {
      const response = await fetch(`${API_URL}/api/clients?page_size=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (error) {
      console.error('Failed to load clients:', error);
    }
  };

  const loadDossiers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/dossiers?status=generated&page_size=200`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDossiers(data.dossiers || []);
      }
    } catch (error) {
      console.error('Failed to load dossiers:', error);
    }
  };

  useEffect(() => {
    loadLinks();
    loadRequirements();
    loadClients();
    loadDossiers();
  }, [loadLinks]);

  const createLink = async () => {
    if (!newLink.requirement_id || !newLink.client_id || newLink.candidate_ids.length === 0) {
      toast.error('Please select requirement, client, and at least one candidate');
      return;
    }
    
    setCreating(true);
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/public-cv-links`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          requirement_id: newLink.requirement_id,
          client_id: newLink.client_id,
          candidate_ids: newLink.candidate_ids,
          expires_in_days: newLink.expires_in_days,
          password: newLink.password || null
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Public link created!');
        
        // Copy to clipboard
        navigator.clipboard.writeText(data.url);
        toast.success('Link copied to clipboard');
        
        setCreateModalOpen(false);
        setNewLink({
          requirement_id: '',
          client_id: '',
          candidate_ids: [],
          expires_in_days: 7,
          password: ''
        });
        loadLinks();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create link');
      }
    } catch (error) {
      toast.error('Failed to create link');
    } finally {
      setCreating(false);
    }
  };

  const revokeLink = async (linkId) => {
    if (!confirm('Are you sure you want to revoke this link? The client will no longer be able to access it.')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/public-cv-links/${linkId}/revoke`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Link revoked');
        loadLinks();
        if (selectedLink?.id === linkId) {
          setSelectedLink({ ...selectedLink, is_revoked: true, revoked_at: new Date().toISOString() });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to revoke link');
      }
    } catch (error) {
      toast.error('Failed to revoke link');
    }
  };

  const openDetailDrawer = async (link) => {
    setSelectedLink(link);
    setDetailDrawerOpen(true);
    setLoadingDecisions(true);
    
    try {
      const response = await fetch(`${API_URL}/api/manual-selections/public-cv-links/${link.id}/decisions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setDecisions(data.decisions || []);
      }
    } catch (error) {
      console.error('Failed to load decisions:', error);
    } finally {
      setLoadingDecisions(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const toggleCandidate = (candidateId) => {
    setNewLink(prev => ({
      ...prev,
      candidate_ids: prev.candidate_ids.includes(candidateId)
        ? prev.candidate_ids.filter(id => id !== candidateId)
        : [...prev.candidate_ids, candidateId]
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy, HH:mm');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (link) => {
    if (link.is_revoked) {
      return <Badge className="bg-red-100 text-red-700">Revoked</Badge>;
    }
    if (link.is_expired) {
      return <Badge className="bg-amber-100 text-amber-700">Expired</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  // Filter dossiers by selected requirement
  const filteredDossiers = newLink.requirement_id
    ? dossiers.filter(d => d.requirement_id === newLink.requirement_id || !d.requirement_id)
    : dossiers;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-purple-600 to-indigo-700 rounded-xl flex items-center justify-center shadow-lg">
              <LinkIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="public-links-title">
                Public CV Links
              </h1>
              <p className="text-slate-500 mt-1">
                Share candidate dossiers with clients for review
              </p>
            </div>
          </div>
          
          <Button
            onClick={() => setCreateModalOpen(true)}
            className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
            data-testid="create-link-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create Link
          </Button>
        </div>

        {/* Manual Selections Banner */}
        <ManualSelectionsBanner />

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-center">
              <Select value={requirementFilter} onValueChange={(v) => { setRequirementFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[200px]" data-testid="requirement-filter">
                  <SelectValue placeholder="Filter by Requirement" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Requirements</SelectItem>
                  {requirements.map((req) => (
                    <SelectItem key={req.id} value={req.id}>{req.position_title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="outline" onClick={() => loadLinks()}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Links List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
              </div>
            ) : links.length === 0 ? (
              <div className="text-center py-16">
                <LinkIcon className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <h3 className="text-lg font-medium text-slate-600">No public links yet</h3>
                <p className="text-slate-500 mb-4">Create a link to share candidates with clients</p>
                <Button onClick={() => setCreateModalOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Link
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50">
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Requirement</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Client</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Candidates</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Status</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Views</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Decisions</th>
                      <th className="text-left px-4 py-3 text-sm font-semibold text-slate-600">Expires</th>
                      <th className="text-right px-4 py-3 text-sm font-semibold text-slate-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {links.map((link) => (
                      <tr 
                        key={link.id} 
                        className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => openDetailDrawer(link)}
                        data-testid={`link-row-${link.id}`}
                      >
                        <td className="px-4 py-3">
                          <span className="font-medium text-slate-900">
                            {link.requirement_title || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-700">{link.client_name || 'Unknown'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-700">{link.candidate_ids?.length || 0}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {getStatusBadge(link)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Eye className="w-4 h-4 text-slate-400" />
                            <span className="text-slate-700">{link.opened_count || 0}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`font-medium ${link.decision_count > 0 ? 'text-green-600' : 'text-slate-400'}`}>
                            {link.decision_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm text-slate-500">
                            {link.expires_at ? formatDistanceToNow(parseISO(link.expires_at), { addSuffix: true }) : '-'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                            {link.is_active && (
                              <>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => copyLink(link.url)}
                                >
                                  <Copy className="w-4 h-4 mr-1" />
                                  Copy
                                </Button>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  onClick={() => window.open(link.url, '_blank')}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 border-t border-slate-200">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create Link Modal */}
      <Dialog open={createModalOpen} onOpenChange={setCreateModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-purple-600" />
              Create Public CV Link
            </DialogTitle>
            <DialogDescription>
              Share selected candidates with a client for review. They can approve, reject, or request interviews.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Requirement Selection */}
            <div>
              <Label>Requirement *</Label>
              <Select 
                value={newLink.requirement_id} 
                onValueChange={(v) => setNewLink(prev => ({ ...prev, requirement_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select requirement..." />
                </SelectTrigger>
                <SelectContent>
                  {requirements.map((req) => (
                    <SelectItem key={req.id} value={req.id}>{req.position_title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Client Selection */}
            <div>
              <Label>Client *</Label>
              <Select 
                value={newLink.client_id} 
                onValueChange={(v) => setNewLink(prev => ({ ...prev, client_id: v }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select client..." />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((client) => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Candidate Selection */}
            <div>
              <Label>Select Candidates * ({newLink.candidate_ids.length} selected)</Label>
              <div className="mt-2 border rounded-lg max-h-60 overflow-y-auto">
                {filteredDossiers.length === 0 ? (
                  <div className="p-4 text-center text-slate-500">
                    No generated dossiers available
                  </div>
                ) : (
                  filteredDossiers.map((dossier) => (
                    <div
                      key={dossier.id}
                      className={`flex items-center gap-3 p-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 ${
                        newLink.candidate_ids.includes(dossier.id) ? 'bg-purple-50' : ''
                      }`}
                      onClick={() => toggleCandidate(dossier.id)}
                    >
                      <Checkbox
                        checked={newLink.candidate_ids.includes(dossier.id)}
                        onCheckedChange={() => toggleCandidate(dossier.id)}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-slate-900">
                          {dossier.candidate_name || dossier.parsed_json?.full_name || 'Unknown'}
                        </p>
                        <p className="text-xs text-slate-500">
                          {dossier.resume_number || 'No resume number'} 
                          {dossier.parsed_json?.current_title && ` • ${dossier.parsed_json.current_title}`}
                        </p>
                      </div>
                      {dossier.resume_number && (
                        <Badge variant="outline" className="text-xs font-mono">
                          {dossier.resume_number}
                        </Badge>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
            
            {/* Expiry */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expires In</Label>
                <Select 
                  value={newLink.expires_in_days.toString()} 
                  onValueChange={(v) => setNewLink(prev => ({ ...prev, expires_in_days: parseInt(v) }))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="3">3 days</SelectItem>
                    <SelectItem value="7">7 days</SelectItem>
                    <SelectItem value="14">14 days</SelectItem>
                    <SelectItem value="30">30 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Password Protection (Optional)</Label>
                <Input
                  type="password"
                  placeholder="Leave empty for no password"
                  value={newLink.password}
                  onChange={(e) => setNewLink(prev => ({ ...prev, password: e.target.value }))}
                  className="mt-1"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={createLink} 
              disabled={creating || !newLink.requirement_id || !newLink.client_id || newLink.candidate_ids.length === 0}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Create & Copy Link
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Drawer */}
      <Sheet open={detailDrawerOpen} onOpenChange={setDetailDrawerOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center gap-2">
              <LinkIcon className="w-5 h-5 text-purple-600" />
              Link Details
            </SheetTitle>
          </SheetHeader>
          
          {selectedLink && (
            <div className="mt-6 space-y-6">
              {/* Status & Actions */}
              <div className="flex items-center justify-between">
                {getStatusBadge(selectedLink)}
                <div className="flex gap-2">
                  {selectedLink.is_active && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => copyLink(selectedLink.url)}>
                        <Copy className="w-4 h-4 mr-1" />
                        Copy Link
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => revokeLink(selectedLink.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Ban className="w-4 h-4 mr-1" />
                        Revoke
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* Link Info */}
              <Card>
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Requirement</span>
                    <span className="font-medium">{selectedLink.requirement_title}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Client</span>
                    <span className="font-medium">{selectedLink.client_name}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Candidates</span>
                    <span className="font-medium">{selectedLink.candidate_ids?.length || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Views</span>
                    <span className="font-medium">{selectedLink.opened_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Created</span>
                    <span className="text-sm">{formatDate(selectedLink.created_at)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">Expires</span>
                    <span className="text-sm">{formatDate(selectedLink.expires_at)}</span>
                  </div>
                  {selectedLink.last_opened_at && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-slate-500">Last Viewed</span>
                      <span className="text-sm">{formatDate(selectedLink.last_opened_at)}</span>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Decisions */}
              <div>
                <h3 className="font-semibold text-slate-900 mb-3">Client Decisions</h3>
                {loadingDecisions ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                  </div>
                ) : decisions.length === 0 ? (
                  <div className="text-center py-8 bg-slate-50 rounded-lg">
                    <Clock className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-slate-500">No decisions yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {decisions.map((decision) => (
                      <Card key={decision.id} className={
                        decision.decision === 'approved' ? 'border-green-200 bg-green-50' :
                        decision.decision === 'rejected' ? 'border-red-200 bg-red-50' :
                        'border-indigo-200 bg-indigo-50'
                      }>
                        <CardContent className="pt-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                {decision.decision === 'approved' && <CheckCircle2 className="w-5 h-5 text-green-600" />}
                                {decision.decision === 'rejected' && <XCircle className="w-5 h-5 text-red-600" />}
                                {decision.decision === 'schedule_requested' && <Calendar className="w-5 h-5 text-indigo-600" />}
                                <span className="font-medium">
                                  {decision.candidate_name || 'Unknown'}
                                </span>
                              </div>
                              {decision.resume_number && (
                                <p className="text-xs text-slate-500 mt-1 font-mono">{decision.resume_number}</p>
                              )}
                            </div>
                            <Badge className={
                              decision.decision === 'approved' ? 'bg-green-100 text-green-700' :
                              decision.decision === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-indigo-100 text-indigo-700'
                            }>
                              {decision.decision === 'approved' ? 'Approved' :
                               decision.decision === 'rejected' ? 'Rejected' : 'Interview'}
                            </Badge>
                          </div>
                          {decision.notes && (
                            <p className="mt-2 text-sm text-slate-600 bg-white/50 p-2 rounded">
                              {decision.notes}
                            </p>
                          )}
                          {decision.preferred_slots?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium text-slate-500">Preferred times:</p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {decision.preferred_slots.map((slot, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {slot.date} {slot.time}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                          <p className="text-xs text-slate-400 mt-2">
                            {formatDate(decision.decided_at)}
                            {decision.geo_country && ` • ${decision.geo_city || ''} ${decision.geo_country}`}
                          </p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
